import React, { Fragment, useMemo } from 'react';
import { Col, Divider, Row, Typography } from 'antd';

import MetricLabel from 'components/MetricLabel';
import MetricTooltip from 'components/MetricTooltip';
import MetricValue from 'components/MetricValue';
import MetricChange from 'components/MetricChange';
import SegmentTag from 'components/SegmentTag';
import CenterContent from 'components/CenterContent';
import DimensionLabel from 'components/DimensionLabel';
import DimensionValue from 'components/DimensionValue';
import { getTableData } from './utils';

const { Text } = Typography;

const SegmentResultTitle = ({ row, dimensions }) => (
  <CenterContent horizontal style={{ padding: '8px 0' }}>
    {dimensions.map(dimension => (
      <Fragment key={dimension.name}>
        <b><DimensionLabel {...dimension} lineBreaks={false} /></b>
        <DimensionValue dimension={dimension} value={row[dimension.name]} truncate={false} />
      </Fragment>
    ))}
    <SegmentTag segment={row.segment} showDateRange />
  </CenterContent>
);

const MetricItem = ({ metric, row, showChangeOverTime }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Text style={{ fontSize: '24px', whiteSpace: 'nowrap' }}>
      <MetricValue metric={metric} row={row} />
    </Text>
    <Text type="secondary" style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
      <MetricLabel {...metric} />
    </Text>
    {showChangeOverTime && <MetricChange metric={metric} row={row} aligned={false} />}
  </div>
);

const WidgetRow = ({ row, metrics, dimensions, showChangeOverTime }) => (
  <>
    <SegmentResultTitle row={row} dimensions={dimensions} />
    <Row gutter={16} justify="space-evenly">
      {metrics.map(metric => (
        <Col key={metric.name}>
          <MetricTooltip
            metric={metric}
            dimensions={dimensions}
            row={row}
          >
            <div>
              <MetricItem metric={metric} row={row} showChangeOverTime={showChangeOverTime} />
            </div>
          </MetricTooltip>
        </Col>
      ))}
    </Row>
  </>
);

const WidgetResult = ({ results, config, mainSegment, comparisonSegments }) => {
  const dimensions = config?.dimensions || [];
  const metrics = config?.metrics || [];
  const tableData = useMemo(() => getTableData({ dimensions, mainSegment, comparisonSegments, results }), [results, dimensions, mainSegment, comparisonSegments]);

  return (
    tableData.map((row, i) => (
      <Fragment key={row.id}>
        {i > 0 && <Divider />}
        <WidgetRow row={row} metrics={metrics} dimensions={dimensions} showChangeOverTime />
        {row.segmentComparisonResults.map((comparison) => (
          <Fragment key={comparison.id}>
            <Divider style={{ margin: '16px 0' }} />
            <WidgetRow key={comparison.id} row={comparison} metrics={metrics} dimensions={dimensions} showChangeOverTime />
          </Fragment>
        ))}
      </Fragment>
    ))
  );
};

export default WidgetResult;
